import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useSnackbar } from 'notistack';
import { TITLES, MESSAGES } from '../../../shared/constants';
import DataTable from '../../../shared/data-table';
import {
  updateAllOptionsByProfile,
  getProfilesByEmployeePosition,
  getAllProfiles,
  updateAllProfilesByPosition,
  getApplications,
  getProfilesByApplication
} from './logic';
import ManagementPositionOptionDetail from '../management-position/management-position-options-detail';
import ManagementPositionDetail from '../management-position/management-position-detail';
import Edit from '@mui/icons-material/Edit';
import ErrorSnackbar from '../../error-snackbar';
import BackDrop from '../../../shared/backdropDE';
import { getUser } from '../../../shared/auth-service';

const ManageProfileOptions = () => {
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const [employeePosition, setEmployeePosition] = useState(null);
  const [data, setData] = useState(null);
  const [employeePositionId, setEmployeePositionId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [optionDetails, setOptionDetails] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);
  const [loadingOptionDetails, setLoadingOptionDetails] = useState(false);
  const [loadingProfileDetails, setLoadingProfileDetails] = useState(false);
  const [applications, setApplications] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);
  const [uniquePositions, setUniquePositions] = useState([]);
  const [modalType, setModalType] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [cargoFilter, setCargoFilter] = useState('');
  const [uniquePositionsFilter, setUniquePositionsFilter] = useState([]);
  const [profileFilter, setProfileFilter] = useState(null);
  const [applicationFilter, setApplicationFilter] = useState(null);
  const [filteredProfileOptions, setFilteredProfileOptions] = useState([]);
  const searchCargoRef = React.useRef(null);
  const companyId = user.countryCode.split('-')[0];
  const columns = [
    { field: 'employeePosition', headerName: 'Cargo', flex: 1 },
    {
      field: 'totalPerProfile',
      headerName: 'Perfiles Asignados',
      type: 'number',
      minWidth: 150,
      flex: 0.1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "Editar",
      headerName: "Editar",
      width: 60,
      sortable: false,
      renderCell: (cellValues) => {
        return <IconButton
          color="info"
          onClick={(e) => { handleEdit(e, cellValues) }}
        >
          <Edit />
        </IconButton>;
      }
    },
  ];

  const getUniquePositions = (data) => {
    const unique = {};
    data.forEach((item) => {
      if (!unique[item.employeePosition]) {
        unique[item.employeePosition] = item;
      }
    });
    //console.log(unique);
    return Object.values(unique);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loading) {
        try {
          const rawData = await getProfilesByEmployeePosition(companyId);
          const profiles = await getAllProfiles(companyId);
          const applications = await getApplications(companyId);
          setApplications(applications);
          // Calcula el total por cada employeePositionId con descripción no vacía
          const totalsPerId = rawData.reduce((acc, item) => {
            // Considera solo los items con descripción no vacía
            if (item.description !== '') {
              const key = item.employeePositionId; // Usa employeePositionId como clave
              acc[key] = (acc[key] || 0) + 1; // Incrementa el contador para el id
            }
            return acc;
          }, {});
  
          // Agrega el total a cada elemento basado en su employeePositionId
          // Solo para los registros con descripción no vacía
          const dataWithTotals = rawData.map((item) => {
            return item.description !== '' ? {
              ...item,
              totalPerProfile: totalsPerId[item.employeePositionId], // Agrega el total basado en el id
            } : item;
          });
  
          setData(dataWithTotals);
          setProfiles(profiles);
        } catch (error) {
          console.error("Error loading data:", error);
          setError(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  });

  useEffect(() => {
    if (data) {
      setUniquePositions(getUniquePositions(data));
    }
  }, [data]);

  useEffect(() => {
    const loadProfiles = async () => {
      if (applicationFilter) {
        const filtered = await getProfilesByApplication(applicationFilter.id, companyId);
        setFilteredProfileOptions(filtered || []);
      } else {
        setFilteredProfileOptions([]);
        setProfileFilter(null);
      }
    };
    loadProfiles();
  }, [applicationFilter]);

  // Filtro reactivo — se recalcula ante cualquier cambio de filtro
  useEffect(() => {
    if (!data || !uniquePositions.length) return;

    let result = uniquePositions;

    if (cargoFilter) {
      const search = cargoFilter.toUpperCase();
      result = result.filter(item =>
        item.employeePosition.toUpperCase().includes(search)
      );
    }

    if (applicationFilter && filteredProfileOptions.length > 0) {
      const profileDescriptions = new Set(filteredProfileOptions.map(p => p.description));
      const matchingIds = new Set(
        data.filter(item => profileDescriptions.has(item.description))
            .map(item => item.employeePositionId)
      );
      result = result.filter(item => matchingIds.has(item.employeePositionId));
    }

    if (profileFilter) {
      const matchingIds = new Set(
        data.filter(item => item.description === profileFilter.description)
            .map(item => item.employeePositionId)
      );
      result = result.filter(item => matchingIds.has(item.employeePositionId));
    }

    setUniquePositionsFilter(result);
  }, [cargoFilter, profileFilter, applicationFilter, filteredProfileOptions, uniquePositions, data]);

  const applyFilters = () => {}; // mantenido por compatibilidad, ya no se usa

  const handleFilterCargo = (value) => {
    setCargoFilter(value);
  };

  const fetchOptionDetails = async (employeePositionId) => {
    //console.log("fetchOptionDetails called with id:", employeePositionId);
    setLoadingOptionDetails(true);
    try {
      const details = await getProfilesByEmployeePosition(companyId,employeePositionId);
      //console.log("Option details:", details);
      setOptionDetails(details);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoadingOptionDetails(false);
      setModalType('optionDetail'); 
    }
  };

  const fetchProfileDetails = async (employeePositionId) => {
    //console.log("fetchProfileDetails called with id:", employeePositionId);
    setLoadingProfileDetails(true);
    try {
      const details = await getProfilesByEmployeePosition(companyId,employeePositionId);
      //console.log("Profile details:", details);
      setProfileDetails(details);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoadingProfileDetails(false);
      setModalType('profileDetail');
    }
  };

  const handleEdit = (e, cellValues) => {
    e.stopPropagation(); 
    setEmployeePosition(cellValues.row.employeePosition)
    setEmployeePositionId(cellValues.row.employeePositionId);
    fetchOptionDetails(cellValues.row.employeePositionId); 
    setShowModal(true);
  }

  const handleView = (e, cellValues) => {
    e.stopPropagation(); 
    setEmployeePosition(cellValues.row.employeePosition)
    fetchProfileDetails(cellValues.row.employeePositionId); 
  }

  const handleSave = async (profiles) => {
    //console.log('FUNCIONO-handleSave');
    //console.log(profiles);
    await updateAllProfilesByPosition(profiles, employeePositionId, user.employeeId);
    fetchOptionDetails(employeePositionId);
    setShowModal(false);
    setLoading(true);
    enqueueSnackbar(MESSAGES.SAVED);
  }

  if (loading) {
    return (
      // <Box sx={{ display: 'flex' }}>
      //     <CircularProgress />
      // </Box>
      <BackDrop />
    );
  }

  if (error) {
    return (<ErrorSnackbar message={error} />);
  }  

  return (
    <>
     {modalType === 'optionDetail' && showModal &&(
      < ManagementPositionOptionDetail
        data={optionDetails}
        employeePositionId={employeePositionId}
        employeePosition={employeePosition}
        profiles={profiles}
        applications={applications}
        handleClose={() => setModalType(null)}
        handleSave={handleSave}
      />
    )}

    {modalType === 'profileDetail' && (
      <ManagementPositionDetail
        data={optionDetails}
        employeePositionId={employeePositionId}
        employeePosition={employeePosition}
        profiles={profiles}
        handleClose={() => setModalType(null)}
      />
    )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1, px: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h5" style={{ color: '#1a3072' }} fontWeight="bold">
            Perfiles por <span style={{ color: '#00b0ef' }}>Cargo</span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title="Filtrar por nombre de cargo">
            <FormControl sx={{ width: 200 }} variant="outlined" size="small">
              <InputLabel htmlFor="outlined-adornment-search-cargo">Buscar cargo</InputLabel>
              <OutlinedInput
                id="outlined-adornment-search-cargo"
                inputRef={searchCargoRef}
                type="text"
                value={cargoFilter}
                onChange={(e) => setCargoFilter(e.target.value)}
                endAdornment={
                  cargoFilter
                    ? <IconButton size="small" onClick={() => setCargoFilter('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    : <SearchIcon sx={{ color: '#aaa' }} />
                }
                label="Buscar cargo"
              />
            </FormControl>
          </Tooltip>
          <Autocomplete
            size="small"
            sx={{ width: 200 }}
            options={applications}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={applicationFilter}
            onChange={(_, newValue) => {
              setApplicationFilter(newValue);
            }}
            renderInput={(params) => <TextField {...params} label="Aplicación" variant="outlined" />}
            noOptionsText="Sin resultados"
          />
          <Autocomplete
            size="small"
            sx={{ width: 200 }}
            options={filteredProfileOptions}
            getOptionLabel={(o) => o.description || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={profileFilter}
            disabled={!applicationFilter}
            onChange={(_, newValue) => {
              setProfileFilter(newValue);
              applyFilters(undefined, newValue, undefined);
            }}
            renderInput={(params) => (
              <TextField {...params} label={applicationFilter ? 'Perfil' : 'Seleccione aplicación'} variant="outlined" />
            )}
            noOptionsText="Sin resultados"
          />
        </Box>
      </Box>

      <Box sx={{ p: '32px 0px' }}>
        <DataTable columns={columns} rows={!cargoFilter && !applicationFilter && !profileFilter ? uniquePositions : uniquePositionsFilter}
         sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold', // Aplicar negrita a los títulos de las columnas
          },
        }} />
      </Box>
    </>
  );

};

export default ManageProfileOptions;